import { decodeJwt } from 'jose';

/** Refresh this many ms before JWT `exp` (clock skew + network latency). */
export const DEFAULT_TOKEN_REFRESH_BUFFER_MS = 60_000;

export function isTokenExpiringSoon(
  token: string | null | undefined,
  isTokenValidFn: (token: string) => boolean,
  bufferMs = DEFAULT_TOKEN_REFRESH_BUFFER_MS,
): boolean {
  if (!token) {
    return true;
  }
  if (!isTokenValidFn(token)) {
    return true;
  }
  try {
    const { exp } = decodeJwt(token);
    if (!exp) {
      return true;
    }
    return Date.now() >= exp * 1000 - bufferMs;
  } catch {
    return true;
  }
}

export function normalizeErrorBody(errorBody: unknown): string {
  if (errorBody == null) {
    return '';
  }
  if (typeof errorBody === 'string') {
    return errorBody;
  }
  if (typeof errorBody === 'object') {
    const record = errorBody as Record<string, unknown>;
    const parts = [
      record['message'],
      record['error'],
      record['error_description'],
      record['errorMessage'],
    ]
      .filter((v) => typeof v === 'string')
      .join(' ');
    return parts || JSON.stringify(errorBody);
  }
  return String(errorBody);
}

const AUTH_FAILURE_PATTERNS = [
  /expired/i,
  /unauthorized/i,
  /not authorized/i,
  /invalid.*token/i,
  /missing authentication token/i,
  /token has expired/i,
  /jwt expired/i,
];

const ACCESS_DENIED_PATTERNS = [
  /access denied/i,
  /insufficient permissions/i,
  /permission denied/i,
  /forbidden resource/i,
];

export function matchesAuthFailureMessage(errorBody: unknown): boolean {
  const text = normalizeErrorBody(errorBody);
  if (!text) {
    return false;
  }
  return AUTH_FAILURE_PATTERNS.some((pattern) => pattern.test(text));
}

export function matchesAccessDeniedMessage(errorBody: unknown): boolean {
  const text = normalizeErrorBody(errorBody);
  if (!text) {
    return false;
  }
  return ACCESS_DENIED_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Whether an HTTP error should trigger a Cognito refresh + retry.
 * API Gateway often returns 403 (not 401) for expired JWTs.
 */
export function shouldRefreshOnHttpError(
  status: number,
  errorBody: unknown,
  idToken: string | null,
  isTokenValidFn: (token: string) => boolean,
): boolean {
  if (idToken && !isTokenValidFn(idToken)) {
    return true;
  }

  if (status === 401) {
    return true;
  }

  if (status === 403) {
    if (matchesAccessDeniedMessage(errorBody)) {
      return false;
    }
    if (matchesAuthFailureMessage(errorBody)) {
      return true;
    }
    // Unknown 403 with a bearer token: attempt refresh once (retry guard prevents loops).
    return !!idToken;
  }

  return false;
}

export function isCognitoTokenError(error: unknown): boolean {
  const err = error as { name?: string; code?: string; message?: string };
  const code = err?.name || err?.code || '';
  if (code === 'NotAuthorizedException' || code === 'ExpiredTokenException') {
    return true;
  }
  const message = err?.message ?? '';
  return /expired|not authorized|invalid.*token/i.test(message);
}

/** Definitive auth failures — safe to clear session. Network errors are not fatal. */
export function isAuthFatalError(error: unknown): boolean {
  const err = error as { name?: string; code?: string; message?: string };
  const code = err?.name || err?.code || '';
  const message = err?.message ?? '';

  if (message.includes('No refresh token')) {
    return true;
  }
  if (message.includes('Failed to save refreshed session')) {
    return true;
  }
  if (
    code === 'NotAuthorizedException' ||
    code === 'InvalidParameterException' ||
    code === 'UserNotFoundException'
  ) {
    return true;
  }
  if (code === 'NetworkingError' || code === 'NetworkError') {
    return false;
  }
  if (message.includes('Failed to fetch') || message.includes('Network')) {
    return false;
  }
  return false;
}
